import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../api'
import Pager from '../components/Pager'
import type { Paged } from '../types'
import { when } from '../util'

const LIMIT = 25

interface BillingRow {
  user_id: string
  email: string
  created_at: string
  subscription_status: string | null
  current_period_end: string | null
  entitlement_source: string | null
  active_until: string | null
  homes: number
  coupons: string | null
}

interface Coupon {
  code: string
  kind: string
  value: number | null
  max_redemptions: number | null
  redeemed_count: number
  expires_at: string | null
  disabled_at: string | null
  note: string | null
  created_at: string
  created_by: string | null
}

const SUB_CHIP: Record<string, string> = {
  active: 'on', trialing: 'on', past_due: '',
  canceled: '', unpaid: 'danger', incomplete: '',
}

export default function Billing() {
  const qc = useQueryClient()
  const [code, setCode] = useState('')
  const [kind, setKind] = useState('free_forever')
  const [days, setDays] = useState('30')
  const [max, setMax] = useState('')
  const [err, setErr] = useState('')

  const [offset, setOffset] = useState(0)
  const rows = useQuery<Paged<BillingRow>>({
    queryKey: ['adminBilling', offset],
    queryFn: () => api<Paged<BillingRow>>(
      `/admin/billing?limit=${LIMIT}&offset=${offset}`),
  })
  const coupons = useQuery<Coupon[]>({
    queryKey: ['coupons'], queryFn: () => api<Coupon[]>('/admin/coupons'),
  })

  const create = useMutation({
    mutationFn: () => api('/admin/coupons', {
      method: 'POST',
      body: JSON.stringify({
        code: code.trim().toUpperCase(),
        kind,
        value: kind === 'trial_days' ? Number(days) : null,
        max_redemptions: max ? Number(max) : null,
      }),
    }),
    onSuccess: () => {
      setCode(''); setErr('')
      qc.invalidateQueries({ queryKey: ['coupons'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  const disable = useMutation({
    mutationFn: (c: string) => api(`/admin/coupons/${encodeURIComponent(c)}`,
                                   { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  })

  const grant = useMutation({
    mutationFn: (v: { user_id: string; revoke?: boolean }) =>
      api('/admin/entitlements', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adminBilling'] }),
    onError: (e) => setErr((e as Error).message),
  })

  const list = rows.data?.rows ?? []
  const paying = list.filter((r) => r.subscription_status === 'active').length
  const comped = list.filter((r) => r.entitlement_source
                                 && r.entitlement_source !== 'stripe').length

  return (
    <div className="page">
      <h1>Billing</h1>
      <p className="sub">
        Subscriptions, promotional codes, and complimentary access. All three
        resolve to the same entitlement — the control loop cannot tell them apart.
      </p>

      <div className="stats">
        <div className="stat"><div className="k">Customers</div><div className="v">{rows.data?.total ?? 0}</div></div>
        <div className="stat ok"><div className="k">Paying</div><div className="v">{paying}</div></div>
        <div className="stat"><div className="k">Comped</div><div className="v">{comped}</div></div>
        <div className="stat"><div className="k">Codes</div><div className="v">{(coupons.data ?? []).length}</div></div>
      </div>

      {err && <div className="err">{err}</div>}

      <div className="panel">
        <header>Customers</header>
        {list.length === 0 ? <div className="empty">No customers yet.</div> : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th><th className="num">Homes</th><th>Subscription</th>
                  <th>Renews</th><th>Access</th><th>Codes used</th><th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.user_id}>
                    <td className="strong">{r.email}</td>
                    <td className="num">{r.homes}</td>
                    <td>
                      {r.subscription_status
                        ? <span className={`chip ${SUB_CHIP[r.subscription_status] ?? ''}`}>
                            {r.subscription_status}</span>
                        : <span className="faint">—</span>}
                    </td>
                    <td className="muted">{when(r.current_period_end)}</td>
                    <td>
                      {r.entitlement_source
                        ? <span className="chip good">{r.entitlement_source}</span>
                        : <span className="chip bare">none</span>}
                      {r.active_until && (
                        <div className="sublabel">until {when(r.active_until)}</div>
                      )}
                    </td>
                    <td className="muted">{r.coupons ?? '—'}</td>
                    <td className="muted">{when(r.created_at)}</td>
                    <td>
                      {r.entitlement_source === 'manual' ? (
                        <button className="btn ghost"
                          onClick={() => grant.mutate({ user_id: r.user_id, revoke: true })}>
                          Revoke comp
                        </button>
                      ) : (
                        <button className="btn ghost"
                          onClick={() => {
                            if (confirm(`Give ${r.email} complimentary access?\n\n`
                              + 'This is recorded in the audit log.'))
                              grant.mutate({ user_id: r.user_id })
                          }}>
                          Comp
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={rows.data?.total ?? 0} limit={LIMIT} offset={offset}
               onOffset={setOffset} noun="customer" />
      </div>

      <div className="panel">
        <header>Promotional codes</header>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <div className="row">
            <input placeholder="CODE" value={code} style={{ width: 160 }}
                   onChange={(e) => setCode(e.target.value.toUpperCase())} />
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="free_forever">Free forever</option>
              <option value="trial_days">Trial</option>
            </select>
            {kind === 'trial_days' && (
              <input type="number" value={days} style={{ width: 90 }}
                     onChange={(e) => setDays(e.target.value)} placeholder="days" />
            )}
            <input type="number" value={max} style={{ width: 130 }}
                   onChange={(e) => setMax(e.target.value)}
                   placeholder="max uses (any)" />
            <button className="btn" disabled={code.trim().length < 4 || create.isPending}
                    onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create code'}
            </button>
          </div>
          <p className="faint" style={{ margin: '10px 0 0', fontSize: 12.5 }}>
            These are ours, not Stripe promotion codes — a Stripe promo still
            needs a card on file. A code here grants access with no Stripe
            object at all.
          </p>
        </div>

        {(coupons.data ?? []).length === 0 ? (
          <div className="empty">No codes yet.</div>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Code</th><th>Kind</th><th className="num">Used</th>
                  <th>Expires</th><th>Status</th><th>Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(coupons.data ?? []).map((c) => (
                  <tr key={c.code}>
                    <td className="strong">{c.code}</td>
                    <td>
                      {c.kind === 'trial_days'
                        ? `${c.value} day trial` : 'Free forever'}
                    </td>
                    <td className="num">
                      {c.redeemed_count}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}
                    </td>
                    <td className="muted">{c.expires_at ? when(c.expires_at) : '—'}</td>
                    <td>
                      {c.disabled_at
                        ? <span className="chip">disabled</span>
                        : <span className="chip good">live</span>}
                    </td>
                    <td className="muted">{when(c.created_at)}</td>
                    <td>
                      {!c.disabled_at && (
                        <button className="btn ghost"
                          onClick={() => {
                            if (confirm(`Disable ${c.code}?\n\n`
                              + 'Nobody new can redeem it. Access already granted '
                              + 'by this code is NOT withdrawn.'))
                              disable.mutate(c.code)
                          }}>
                          Disable
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
